<template>
  <section ref="statisticsSection" class="statistics-section">
    <div class="section-header">
     
      <h2 class="section-title">
        <span>{{ t('Statistics.title') }}</span>
      </h2>
      <p class="section-subtitle">
        {{ t('hero.description') }}
      </p>
    </div>

    <div class="stats-band">
      <div class="stats-grid">
        <div v-for="stat in statistics" :key="stat.labelKey" class="stat-card">
          <div v-if="stat.type === 'icon'" class="icon-ring">
            <FontAwesomeIcon :icon="stat.icon" class="text-2xl" />
          </div>
          <p class="stat-value">{{ formatNumber(stat.currentValue) }}</p>
          <p class="stat-label">{{ t(stat.labelKey) }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faHouseLaptop, faBuilding, faMoneyBillTrendUp, faCheck } from '@fortawesome/free-solid-svg-icons';

const { t, locale } = useI18n();

const statistics = ref([
  { labelKey: 'Statistics.progress', value: 25000, currentValue: 0, icon: faHouseLaptop, type: 'icon' },
  { labelKey: 'Statistics.builtUpArea', value: 54000, currentValue: 0, icon: faBuilding, type: 'icon' },
  { labelKey: 'Statistics.plotArea', value: 90000, currentValue: 0, icon: faMoneyBillTrendUp, type: 'icon' },
  { labelKey: 'Statistics.projectsCompleted', value: 56, currentValue: 0, icon: faCheck, type: 'icon' }
]);

const statisticsSection = ref(null);

const countUp = (stat) => {
  const duration = 2000;
  const frames = 60;
  const increment = Math.ceil(stat.value / frames);

  let current = 0;
  const updateCounter = () => {
    current += increment;
    stat.currentValue = Math.min(current, stat.value);
    if (current < stat.value) {
      setTimeout(updateCounter, duration / frames);
    }
  };

  updateCounter();
};

const formatNumber = (value) => {
  return new Intl.NumberFormat(locale.value === 'ar' ? 'ar-SA' : 'en-US').format(value);
};

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        statistics.value.forEach(countUp);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  if (statisticsSection.value) observer.observe(statisticsSection.value);
});
</script>

<style scoped>
.statistics-section {
  padding: 5rem 1.5rem;
  margin: 0 auto;
  max-width: 1200px;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.stats-band {
  position: relative;
  padding: 3rem;
  border-radius: 40px;
  background: linear-gradient(120deg, rgba(253, 143, 66, 0.12), rgba(91, 91, 91, 0.08));
  overflow: hidden;
}

.stats-band::after {
  content: '';
  position: absolute;
  inset: -30% auto auto 60%;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(253, 143, 66, 0.35), transparent 70%);
  filter: blur(40px);
  opacity: 0.6;
  pointer-events: none;
}

.stats-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  z-index: 1;
}

.stat-card {
  padding: 2rem;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(253, 143, 66, 0.18);
  box-shadow: 0 30px 70px rgba(10, 10, 10, 0.08);
  text-align: center;
  transition: transform 0.3s ease, border-color 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-8px);
  border-color: rgba(253, 143, 66, 0.4);
}

.icon-ring {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(253, 143, 66, 0.15);
  color: var(--brand-primary);
}

.stat-value {
  font-size: 2.75rem;
  font-weight: 700;
  color: var(--brand-secondary);
  margin: 0.25rem 0;
}

.stat-label {
  color: rgba(31, 31, 31, 0.6);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 600;
}

.dark .stats-band {
  background: linear-gradient(120deg, rgba(253, 143, 66, 0.1), rgba(8, 11, 16, 0.8));
}

.dark .stat-card {
  background: rgba(15, 23, 42, 0.85);
  color: #f5f5f5;
}

.dark .stat-label {
  color: rgba(255, 255, 255, 0.6);
}
</style>
